import { useState } from "react";
import { FileText } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Spinner } from "./icons";
import { UploadButtons } from "./UploadButtons";
import type { PagePreview } from "../App";

interface ScreenPartialProps {
  pages: PagePreview[];
  onAddPage: (file: File) => void;
  onFinalize: () => void;
  finalizing?: boolean;
}

export function ScreenPartial({
  pages,
  onAddPage,
  onFinalize,
  finalizing = false,
}: ScreenPartialProps) {
  return (
    <div className="w-full max-w-[400px]">
      <div className="mb-5 text-center">
        <h1 className="text-xl font-semibold text-brand-700">
          {pages.length === 1 ? "1 page ajoutée" : `${pages.length} pages ajoutées`}
        </h1>
        <p className="mt-1 text-[14px] text-gray-500">
          Ce document comporte-t-il d'autres pages ?
        </p>
      </div>

      <div className="mb-5 flex flex-wrap gap-3">
        {pages.map((page, i) => (
          <PageThumbnail key={i} page={page} index={i} />
        ))}
      </div>

      <UploadButtons onFile={onAddPage} />

      <Button
        className="mt-3 w-full"
        onClick={onFinalize}
        disabled={finalizing}
      >
        {finalizing && <Spinner />}
        Terminer et envoyer
      </Button>
    </div>
  );
}

function PageThumbnail({ page, index }: { page: PagePreview; index: number }) {
  const isImage = page.mimeType.startsWith("image/");
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = isImage && !imgFailed;

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex h-24 w-20 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {showImage ? (
          <img
            src={page.url}
            alt=""
            className="h-full w-full object-cover"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="flex flex-col items-center gap-1 text-gray-400">
            <FileText className="h-8 w-8" />
            <span className="text-[10px]">PDF</span>
          </div>
        )}
      </div>
      <span className="text-[12px] font-medium text-gray-500">Page {index + 1}</span>
    </div>
  );
}
